import { MkIcon } from "@/mk/MkIcon";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { DESIGN_DOC_MODELS, DESIGN_TILE_LABELS, DOC_SECTIONS, DOC_TILES,
         PROJECT_STATES } from "./projectCardSpec";
import { loadProjectCard } from "./projectCards";

export interface ProjectRow {
    id: number;
    name: string;
    code?: string;
    state?: string;
    elapsed_period_percentage?: number;
    project_type?: string;
    portfolio_id?: { id: number; name?: string; display_name?: string } | false;
    portfolio_code?: string | false;
    portfolio_color?: string | false;
}

export type ProjectStates = Record<string, { label: string; color: string }>;

interface Party {
    id: number;
    name: string;
    role: string;
    image?: string | false;
}

interface DesignCard {
    progress: number;
    items_progress?: number;
    contract_value: number;
    claimed: number;
    phases: number;
    phases_done: number;
    next_phase: string;
    next_phase_date: string;
    execution_project: { id: number; name: string } | false;
}

interface CardData {
    design?: DesignCard;
    achievement: number;
    counts: Record<string, number>;
    states: Record<string, string>;
    /** "mine" when the reader follows only the documents waiting for them or raised by them */
    scope?: Record<string, "all" | "mine">;
    board: {
        code?: string;
        elapsed?: number;
        started?: boolean;
        has_end?: boolean;
        days_left?: number | false;
        owner?: Party;
        consultant?: Party;
        pmc?: Party;
        contractor?: Party;
    };
    has_image: boolean;
    pending_total: number;
    last_activity: string;
}

const EMPTY: CardData = {
    achievement: 0, counts: {}, states: {}, board: {},
    has_image: false, pending_total: 0, last_activity: "",
};

const PARTY_ROLES: [keyof CardData["board"], string, string][] = [
    ["owner", "card.role.owner", "fa-building-o"],
    ["consultant", "card.role.consultant", "fa-user-circle-o"],
    ["pmc", "card.role.pmc", "fa-sitemap"],
    ["contractor", "card.role.contractor", "fa-wrench"],
];

function percent(value: number | undefined | null): number {
    return Math.min(100, Math.max(0, Math.round(value || 0)));
}

function Illustration() {
    return (
        <svg className="pm-pcard-photo-svg" viewBox="0 0 320 120"
             preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false">
            <rect className="pm-pcard-svg-bg" width="320" height="120" />
            <g className="pm-pcard-svg-ink" transform="translate(48 34) scale(0.7)">
                <rect x="34" y="60" width="54" height="48" rx="3" opacity=".42" />
                <rect x="96" y="34" width="66" height="74" rx="3" opacity=".62" />
                <rect x="170" y="52" width="48" height="56" rx="3" opacity=".38" />
                <rect x="226" y="70" width="58" height="38" rx="3" opacity=".3" />
                <rect x="20" y="106" width="280" height="3" rx="1.5" opacity=".5" />
            </g>
            <g className="pm-pcard-svg-cut" transform="translate(48 34) scale(0.7)">
                {[[46, 70], [64, 70], [46, 88], [64, 88]].map(([x, y]) => (
                    <rect key={`a${x}-${y}`} x={x} y={y} width="12" height="10" rx="2" />
                ))}
                {[[108, 46], [130, 46], [108, 66], [130, 66], [108, 86], [130, 86]].map(([x, y]) => (
                    <rect key={`b${x}-${y}`} x={x} y={y} width="14" height="11" rx="2" />
                ))}
                {[[180, 64], [198, 64], [180, 84], [198, 84], [238, 82], [256, 82]].map(([x, y]) => (
                    <rect key={`c${x}-${y}`} x={x} y={y} width="12" height="10" rx="2" />
                ))}
            </g>
        </svg>
    );
}

export function PmProjectCard({ row, states = PROJECT_STATES, onOpen, documents = true }: {
    row: ProjectRow;
    states?: ProjectStates;
    onOpen?(row: ProjectRow): void;
    /** a portfolio shows the project and its indicators only, without the document sections */
    documents?: boolean;
}) {
    const { t } = useTranslation();
    const [photoFailed, setPhotoFailed] = useState(false);
    const [logoFailed, setLogoFailed] = useState<Record<string, boolean>>({});
    const query = useQuery<{ data: CardData }>({
        queryKey: ["project-card", row.id],
        queryFn: () => loadProjectCard<CardData>(row.id),
        retry: false,
    });

    const card = query.data?.data ?? EMPTY;
    const board = card.board ?? {};

    const achievement = percent(card.achievement);
    const elapsed = percent(board.elapsed ?? row.elapsed_period_percentage);
    const started = board.started !== undefined ? Boolean(board.started) : elapsed > 0;
    const gap = achievement - elapsed;

    const stateSpec = states[row.state ?? ""];
    const stateLabel = stateSpec ? t(stateSpec.label) : (row.state ?? "");
    const stateColor = stateSpec?.color ?? "secondary";

    const track = !started
        ? { label: t("card.notStarted"), color: "secondary", icon: "fa-hourglass-o" }
        : gap >= 0 ? { label: t("card.onTrack"), color: "success", icon: "fa-check" }
        : gap >= -10 ? { label: t("card.slightlyBehind"), color: "warning", icon: "fa-exclamation" }
        : { label: t("card.behind"), color: "danger", icon: "fa-exclamation-triangle" };

    const days = board.days_left;
    const daysFact = (!board.has_end || typeof days !== "number" || !Number.isInteger(days))
        ? { label: t("card.daysLeft"), value: "", tone: "" }
        : days < 0
            ? { label: t("card.overdueBy"), value: t("card.daysValue", { value: Math.abs(days) }),
                tone: "is-danger" }
            : { label: t("card.daysLeft"), value: t("card.daysValue", { value: days }),
                tone: days <= 30 ? "is-warning" : "" };

    const design = card.design;
    const designProgress = percent(design?.progress);
    const itemsProgress = percent(design?.items_progress);
    const paidShare = design && design.contract_value
        ? percent((design.claimed / design.contract_value) * 100) : 0;

    const meters = design ? [
        { key: "items", label: t("card.itemsProgress"), icon: "fa-list-ol",
          value: itemsProgress, display: String(itemsProgress) },
        { key: "design", label: t("card.designProgress"), icon: "fa-pencil-square-o",
          value: designProgress, display: String(designProgress) },
        { key: "paid", label: t("card.designPaid"), icon: "fa-money",
          value: paidShare, display: String(paidShare) },
        { key: "time", label: t("card.elapsed"), icon: "fa-clock-o",
          value: elapsed, display: String(elapsed) },
    ] : [
        { key: "done", label: t("card.achieved"), icon: "fa-check-circle-o",
          value: achievement, display: String(achievement) },
        { key: "time", label: t("card.elapsed"), icon: "fa-clock-o",
          value: elapsed, display: String(elapsed) },
        { key: "gap", label: t("card.deviation"), icon: "fa-line-chart",
          value: Math.abs(gap),
          display: `${gap > 0 ? "+" : gap < 0 ? "-" : ""}${Math.abs(gap)}` },
    ];

    const facts = design ? [
        { key: "value", label: t("card.contractValue"), icon: "fa-briefcase",
          value: design.contract_value ? Math.round(design.contract_value).toLocaleString() : "", tone: "" },
        design.execution_project
            ? { key: "next", label: t("card.executionProject"), icon: "fa-building",
                value: design.execution_project.name, tone: "is-success" }
            : { key: "next", label: t("card.nextPhase"), icon: "fa-flag-checkered",
                value: design.next_phase
                    || (design.phases ? t("card.allPhasesDone") : ""),
                tone: design.next_phase ? "" : design.phases ? "is-success" : "" },
    ] : [
        { key: "days", label: daysFact.label, icon: "fa-calendar-o",
          value: daysFact.value, tone: daysFact.tone },
        { key: "gap", label: t("card.vsPlan"), icon: "fa-line-chart",
          value: started ? `${gap > 0 ? "+" : ""}${gap}%` : "",
          tone: started ? `is-${track.color}` : "" },
    ];

    const pill = !design ? track
        : design.execution_project
            ? { label: t("card.exportedPill"), color: "success", icon: "fa-rocket" }
            : design.phases
                ? { label: t("card.phasesPill", { done: design.phases_done, total: design.phases }),
                    color: "design", icon: "fa-flag-checkered" }
                : { label: t("card.noPhases"), color: "secondary", icon: "fa-flag-o" };

    const parties = PARTY_ROLES
        .filter(([key]) => board[key])
        .map(([key, role, icon]) => {
            const party = board[key] as Party;
            return { key: String(key), role: t(role), icon, name: party.name,
                     image: logoFailed[String(key)] ? "" : party.image || "" };
        });

    const tileByModel = Object.fromEntries(DOC_TILES.map((tile) => [tile.model, tile]));
    const sections = DOC_SECTIONS
        .map((section) => {
            const tiles = section.models
                .filter((model) => model in card.counts)
                .filter((model) => !design || DESIGN_DOC_MODELS.has(model))
                .map((model) => {
                    const tile = tileByModel[model];
                    const count = card.counts[model] ?? 0;
                    const label = design && DESIGN_TILE_LABELS[model] ? DESIGN_TILE_LABELS[model] : tile?.label;
                    return {
                        model,
                        label: label ? t(label) : model,
                        icon: tile?.icon ?? "fa-file-o",
                        route: `${tile?.route ?? "/"}${tile?.route?.includes("?") ? "&" : "?"}project=${row.id}`,
                        count,
                        mine: card.scope?.[model] === "mine",
                    };
                });
            return { ...section, tiles, mine: tiles.every((tile) => tile.mine),
                     total: tiles.reduce((sum, tile) => sum + tile.count, 0) };
        })
        .filter((section) => section.tiles.length);

    const photo = card.has_image && !photoFailed
        ? `/api/v1/files/image/project.project/${row.id}/image_1920?width=640&height=360`
        : "";

    return (
        <article className={`pm-pcard is-${stateColor}`}>
            <div className="pm-pcard-media" role="button" title={row.name}
                 onClick={() => onOpen?.(row)}>
                {photo ? (
                    <img className="pm-pcard-photo" src={photo} alt={row.name}
                         onError={(event) => {
                             event.stopPropagation();
                             setPhotoFailed(true);
                         }} />
                ) : <Illustration />}
                <span className={`pm-pcard-badge is-${stateColor}`}>{stateLabel}</span>
                {design && (row.project_type === "consulting" ? (
                    <span className="pm-pcard-kind"><MkIcon name="fa-lightbulb-o" />{t("workspace.consultingTitle")}</span>
                ) : (
                    <span className="pm-pcard-kind"><MkIcon name="fa-pencil-square-o" />{t("workspace.design")}</span>
                ))}
            </div>

            <div className="pm-pcard-body">
                <header className="pm-pcard-head" role="button" title={row.name}
                        onClick={() => onOpen?.(row)}>
                    <h4 className="pm-pcard-name">
                        {row.portfolio_code ? (
                            <span className="pm-pcard-portfolio"
                                  style={{ ["--pf" as string]: row.portfolio_color || undefined }}
                                  title={row.portfolio_id ? (row.portfolio_id.display_name
                                      || row.portfolio_id.name || "") : ""}>
                                <MkIcon name="fa-folder" />{row.portfolio_code}
                            </span>
                        ) : null}
                        {row.name}
                    </h4>
                </header>

                <div className="pm-pcard-idrow">
                    {parties.length > 0 && (
                        <div className="pm-pcard-parties">
                            {parties.map((party) => (
                                <span className="pm-pcard-party" key={party.key}
                                      title={`${party.role}: ${party.name}`}>
                                    {party.image ? (
                                        <img className="pm-pcard-party-logo" src={party.image} alt={party.name}
                                             onError={(event) => {
                                                 event.stopPropagation();
                                                 setLogoFailed((current) => ({ ...current, [party.key]: true }));
                                             }} />
                                    ) : <MkIcon name={party.icon} />}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="pm-pcard-meta">
                        {board.code && <span className="pm-pcard-code">{board.code}</span>}
                        <span className={`pm-pcard-track is-${pill.color}`}>
                            <MkIcon name={pill.icon} />
                            <span>{pill.label}</span>
                        </span>
                    </div>
                </div>

                <div className="pm-pcard-dash">
                    <div className={`pm-pcard-meters${meters.length > 3 ? " is-four" : ""}`}>
                        {meters.map((meter) => (
                            <div key={meter.key} className={`pm-pcard-meter is-${meter.key} `}
                                 style={{ ["--pm-gauge-v" as string]: meter.value }}>
                                <span className="pm-pcard-dial"><b>{meter.display}</b></span>
                                <span className="pm-pcard-meter-label">
                                    <MkIcon name={meter.icon} />
                                    <span>{meter.label}</span>
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="pm-pcard-facts">
                        {facts.map((fact) => (
                            <div key={fact.key} className={`pm-pcard-fact ${fact.tone}`}>
                                <MkIcon name={fact.icon} />
                                <span className="pm-pcard-fact-body">
                                    <span className="pm-pcard-fact-label">{fact.label}</span>
                                    <span className={`pm-pcard-fact-value${fact.value ? "" : " is-muted"}`}>
                                        {fact.value || "—"}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {documents && <div className="pm-pcard-secs">
                    {sections.map((section) => (
                        <details key={section.id} className={`pm-pcard-sec ${section.total ? "is-hot" : ""}`}>
                            <summary className="pm-pcard-sec-head">
                                <MkIcon name={section.icon} />
                                <span className="pm-pcard-sec-title">{t(section.label)}</span>
                                <span className="pm-pcard-sec-count"
                                      title={section.mine ? t("card.mineScope") : t("card.allScope")}>
                                    {section.total}
                                </span>
                                <MkIcon name="fa-angle-down" className="pm-pcard-sec-arrow" />
                            </summary>
                            <div className="pm-pcard-tiles">
                                {section.tiles.map((tile) => (
                                    <Link key={tile.model} to={tile.route}
                                          title={`${tile.label} — ${tile.mine ? t("card.mineScope") : t("card.allScope")}`}
                                          className={`pm-pcard-tile ${tile.count ? "has-count" : ""}`}>
                                        <MkIcon name={tile.icon} />
                                        <span className="pm-pcard-tile-count">{tile.count}</span>
                                        <span className="pm-pcard-tile-label">{tile.label}</span>
                                    </Link>
                                ))}
                            </div>
                        </details>
                    ))}
                </div>}

                <div className="pm-pcard-foot">
                    {design?.execution_project && (
                        <Link className="pm-pcard-icon-action"
                              to={`/projects?type=execution&open=${design.execution_project.id}`}
                              title={t("card.executionProject")} aria-label={t("card.executionProject")}>
                            <MkIcon name="fa-building" />
                        </Link>
                    )}
                    <Link className="pm-pcard-icon-action" to={`/boq?project=${row.id}`}
                          title={t("card.boq")} aria-label={t("card.boq")}>
                        <MkIcon name="fa-table" />
                    </Link>
                    <Link className="pm-pcard-icon-action" to={`/timeline?project=${row.id}`}
                          title={t("card.timeline")} aria-label={t("card.timeline")}>
                        <MkIcon name="fa-clock-o" />
                    </Link>
                    <button type="button" className="pm-pcard-action is-primary"
                            onClick={() => onOpen?.(row)}>
                        <MkIcon name="fa-folder-open-o" />
                        <span>{t("card.open")}</span>
                    </button>
                </div>
            </div>
        </article>
    );
}
